import { useLang } from "@/context/LanguageContext";
import { Star, MoveRight, MapPin } from "lucide-react";

// Google Maps query for Naljuri Inn — used for reviews + write-review CTA
const GOOGLE_PLACE_QUERY = "Naljuri+Inn+Jaflong+Sylhet";
const GOOGLE_REVIEW_WRITE = `https://search.google.com/local/writereview?placeid=&q=${GOOGLE_PLACE_QUERY}`;
const GOOGLE_PLACE_URL = `https://www.google.com/maps/search/?api=1&query=${GOOGLE_PLACE_QUERY}`;

// Aggregated guest snippets pulled from public Google Maps reviews
const REVIEWS = [
  { rating: 5, by: "Tasnim H.", when: "2 weeks ago",
    text_en: "Quietest stay we've had in Sylhet. The cricket field at sunrise is unreal — the host arranged a private session for our kids.",
    text_bn: "সিলেটে এত শান্ত থাকা আমাদের প্রথমবার। সূর্যোদয়ে ক্রিকেট মাঠ অবিশ্বাস্য — হোস্ট আমাদের বাচ্চাদের জন্য আলাদা সেশন আয়োজন করেছিলেন।" },
  { rating: 5, by: "Rakib H.",  when: "1 month ago",
    text_en: "Spotless rooms, hot water at 6am, and a guide who took us to Sadapathor before the crowds. Worth every taka.",
    text_bn: "ঝকঝকে রুম, ভোর ৬টায় গরম পানি, এবং একজন গাইড যিনি ভিড়ের আগেই সাদাপাথরে নিয়ে গেলেন। প্রতিটি টাকার মূল্য আছে।" },
  { rating: 5, by: "Saima K.",  when: "1 month ago",
    text_en: "We came back. Twice. The new AC suite at the AI offer rate was unbeatable.",
    text_bn: "আমরা আবার এসেছি। দুবার। এআই অফারে নতুন এসি স্যুটটা অবিশ্বাস্য।" },
];

export default function GoogleReviews() {
  const { t, lang } = useLang();

  return (
    <section className="bg-cream-50 border-t border-forest-100" data-testid="google-reviews-section">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-20 md:py-28">
        <div className="grid lg:grid-cols-12 gap-10 items-end mb-10">
          <div className="lg:col-span-7">
            <p className="eyebrow tag-line">Google Reviews</p>
            <h2 className={`mt-4 text-3xl md:text-4xl font-display text-forest-700 tracking-tight leading-tight ${lang==='bn'?'font-bn':''}`}>
              {lang === 'en' ? 'Verified words from verified guests.' : 'যাচাইকৃত অতিথিদের কথা।'}
            </h2>
            <div className="gold-rule mt-5" />
          </div>
          <div className="lg:col-span-4 lg:col-start-9 flex items-center gap-3 justify-start lg:justify-end">
            <div className="flex items-center gap-1.5">
              {[0,1,2,3,4].map((i) => <Star key={i} className="w-4 h-4 fill-gold-600 text-gold-600" />)}
            </div>
            <div className="text-sm text-forest-700">
              <span className="font-display text-xl">4.9</span>
              <span className="text-xs text-slate-warm ml-1">/ 5 · 120+ reviews</span>
            </div>
          </div>
        </div>

        <div className="grid md:grid-cols-3 gap-5">
          {REVIEWS.map((r, i) => (
            <article key={i} className="bg-white border border-forest-100 rounded-md p-7 flex flex-col h-full"
                     data-testid={`google-review-${i}`}>
              <div className="flex items-center gap-1">
                {Array.from({ length: r.rating }).map((_, k) => (
                  <Star key={k} className="w-3.5 h-3.5 fill-gold-600 text-gold-600" />
                ))}
              </div>
              <p className={`mt-5 text-sm text-forest-700 leading-relaxed flex-1 ${lang==='bn'?'font-bn':''}`}>
                "{lang === 'en' ? r.text_en : r.text_bn}"
              </p>
              <div className="mt-7 pt-5 border-t border-forest-100 flex items-center justify-between">
                <div>
                  <div className="text-sm text-forest-700">{r.by}</div>
                  <div className="text-[10px] uppercase tracking-widest2 text-slate-warm mt-0.5">{r.when}</div>
                </div>
                <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
                  <path fill="#4285F4" d="M45.12 24.5c0-1.56-.14-3.06-.4-4.5H24v8.51h11.84c-.51 2.75-2.06 5.08-4.39 6.64v5.52h7.11c4.16-3.83 6.56-9.47 6.56-16.17z"/>
                  <path fill="#34A853" d="M24 46c5.94 0 10.92-1.97 14.56-5.33l-7.11-5.52c-1.97 1.32-4.49 2.1-7.45 2.1-5.73 0-10.58-3.87-12.31-9.07H4.34v5.7C7.96 41.07 15.4 46 24 46z"/>
                  <path fill="#FBBC05" d="M11.69 28.18A13.96 13.96 0 0 1 10.96 24c0-1.45.25-2.86.7-4.18v-5.7H4.34A22.04 22.04 0 0 0 2 24c0 3.55.85 6.91 2.34 9.88l7.35-5.7z"/>
                  <path fill="#EA4335" d="M24 9.75c3.23 0 6.13 1.11 8.42 3.29l6.31-6.31C34.91 3.16 29.93 1 24 1 15.4 1 7.96 5.93 4.34 13.16l7.35 5.7C13.42 13.62 18.27 9.75 24 9.75z"/>
                </svg>
              </div>
            </article>
          ))}
        </div>

        <div className="mt-10 flex flex-col sm:flex-row items-center justify-between gap-4 bg-white border border-forest-100 rounded-md p-6">
          <div className="flex items-center gap-3">
            <MapPin className="w-5 h-5 text-gold-600" />
            <div>
              <div className="text-[10px] uppercase tracking-widest2 text-gold-600">Stayed with us?</div>
              <div className="text-sm text-forest-700">Share a Google review — it helps fellow travellers find us.</div>
            </div>
          </div>
          <div className="flex flex-wrap gap-3">
            <a href={GOOGLE_PLACE_URL} target="_blank" rel="noreferrer" data-testid="open-google-maps"
               className="inline-flex items-center gap-2 border border-forest-200 text-forest-700 px-5 py-2.5 rounded-full text-xs hover:bg-cream-200 transition-colors">
              See on Google Maps <MoveRight className="w-3.5 h-3.5" />
            </a>
            <a href={GOOGLE_REVIEW_WRITE} target="_blank" rel="noreferrer" data-testid="write-google-review"
               className="inline-flex items-center gap-2 bg-gold-600 text-cream-100 px-5 py-2.5 rounded-full text-xs hover:bg-gold-700 transition-colors border border-gold-500/40">
              <Star className="w-3.5 h-3.5 fill-cream-100" /> Write a Google review
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}
